import { array, option, ord, string } from 'fp-ts';
import { pipe } from 'fp-ts/function';
import { useCurrentEnterprise } from '@mews-commander/legacy-entities/src/enterprise';
import { ContentSection, Dash, DividerList, LabeledListItem, TooltippedTag } from '@mews-commander/ui';
import { lt, useLocalizeText, useLocalizeTextOrNull } from '@mews/framework';
import { Stack } from '@optimus-web/core';
import {
    LegalEnvironment,
    ProductClassifications,
    ProductOptions,
    ProductPromotions,
} from '@mews-commander/data-access';
import { upperFirst } from '@mews/std';
import { getLegalEnvironment } from '@mews-commander/legacy-entities/src/ducks/legalEnvironments';
import { useCommanderSelector } from '@mews-commander/utils';
import { WithProductId } from '@mews-commander/types';
import { useProductQuery } from '../../queries/useProductQuery';

const caseInsensitiveByLabel = pipe(
    string.Ord,
    ord.contramap(({ label }: { label: string }) => label.toLowerCase())
);

const useLocalizedFlags = <F extends Record<keyof F, boolean>, N extends string = string>(
    flagValues: F,
    flagName: N
) => {
    const localizeText = useLocalizeText();
    const localizeTextOrNull = useLocalizeTextOrNull();
    return pipe(
        flagValues,
        Object.entries<boolean>,
        array.filterMap(([key, enabled]) => {
            if (enabled) {
                const hint = localizeTextOrNull({
                    textKey: `${flagName}${upperFirst(key)}Hint` as Mews.TextKey,
                });

                return option.some({
                    key,
                    label: localizeText({ textKey: `${flagName}${upperFirst(key)}` as Mews.TextKey }),
                    hint: hint ?? undefined,
                });
            }
            return option.none;
        }),
        array.sort(caseInsensitiveByLabel)
    );
};

export interface FlagTagsProps<F extends Record<keyof F, boolean>, N extends string = string> {
    flagName: N;
    flagValues: F;
}

export const FlagTags = <F extends Record<Extract<keyof F, string>, boolean>, N extends string = string>({
    flagName,
    flagValues,
}: FlagTagsProps<F, N>) => {
    const flags = useLocalizedFlags(flagValues, flagName);
    return (
        <Stack spacing="100" wrap>
            {array.isNonEmpty(flags) ? (
                flags.map(({ key, label, hint }) => (
                    <TooltippedTag key={key} tooltip={hint} variant="basicSubtle" outline>
                        {label}
                    </TooltippedTag>
                ))
            ) : (
                <Dash />
            )}
        </Stack>
    );
};

export const OptionsTabContent = ({ productId }: WithProductId) => {
    const { options, promotions, classifications } = useProductQuery({ productId });
    const { legalEnvironmentCode } = useCurrentEnterprise();
    const { allowProductClassifications }: LegalEnvironment = useCommanderSelector((state) =>
        // @ts-expect-error, badly typed selector
        getLegalEnvironment(state, { legalEnvironmentCode })
    );

    return (
        <ContentSection title={lt('Options')}>
            <DividerList>
                <LabeledListItem
                    label={lt('ProductOptions')}
                    labelOptions={{ whiteSpace: 'nowrap' }}
                    itemAlignment="start"
                >
                    <FlagTags<ProductOptions> flagName="ProductOptions" flagValues={options} />
                </LabeledListItem>
                <LabeledListItem label={lt('Promotions')} itemAlignment="start">
                    <FlagTags<ProductPromotions> flagName="ProductPromotions" flagValues={promotions} />
                </LabeledListItem>
                {allowProductClassifications ? (
                    <LabeledListItem label={lt('Classifications')} itemAlignment="start">
                        <FlagTags<ProductClassifications>
                            flagName="ProductClassifications"
                            flagValues={classifications}
                        />
                    </LabeledListItem>
                ) : null}
            </DividerList>
        </ContentSection>
    );
};
